import { Organisatie, Reisperiode, Routesoort, TVVBestelStatus, TvvStandaardRoute, Voertuigsoort } from "@/entities/tvv";
import type { TvvBesteldeRouteDto, TvvBusDto } from "@/shared/types";
import { formatDateTimeRelative, hourFormatInAmsterdam } from "@/shared/utils";
import { addMinutes, getMinutes, setMinutes, startOfMinute, subMinutes } from "date-fns";
import { Rijtijd } from "./rijtijd";

export type BaseBusEta = {
    stationCode: string;
    tijd: Date;
    bandbreedte: string;
};

type EtaDto = {
    stationcode: string;
    tijd: string;
};

export type TvvBesteldeRoute = {
    id: number;
    dossierId: number;
    status: TVVBestelStatus;
    reisperiode: Reisperiode;
    busFrequentiePerUur?: number;
    bussenBesteld: number;
    aantalBussenOpBeginStation: number;
    stationCodes: string[];
    routeSoort: Routesoort;
    voertuigSoort: Voertuigsoort;
    organisatie: Organisatie;
    opmerking?: string;
    voorwaarden?: string;
    etas?: BaseBusEta[];
    bussen: TvvBus[];
    rijtijden: Rijtijd[];
    totaleRijtijd: number | undefined;
    etasPubliek: boolean;
    isOvRoute: boolean;
    ovLijnNummer?: string | undefined;
    standaardRoute: TvvStandaardRoute | null;
};

export const TvvBesteldeRoute = {
    fromDto(dto: TvvBesteldeRouteDto): TvvBesteldeRoute {
        return {
            id: dto.id,
            dossierId: dto.dossierId,
            status: dto.status,
            reisperiode: dto.reisperiode,
            busFrequentiePerUur: dto.busFrequentiePerUur,
            bussenBesteld: dto.bussenBesteld,
            aantalBussenOpBeginStation: dto.aantalBussenOpBeginStation,
            stationCodes: dto.routeStationCodes,
            routeSoort: dto.routeSoort,
            voertuigSoort: dto.voertuigSoort,
            organisatie: dto.organisatie,
            opmerking: dto.standaardRoute?.opmerking,
            voorwaarden: dto.standaardRoute?.voorwaarde,
            etas: TvvBesteldeRoute.mapAndSortEtas(dto.etas),
            bussen: dto.bussen.map((dto) => TvvBus.fromDto(dto)).sort((a, b) => a.tijd.getTime() - b.tijd.getTime()),
            rijtijden: dto.rijtijden,
            totaleRijtijd: Rijtijd.getTotaleRijtijd(dto.rijtijden),
            etasPubliek: dto.etasPubliek,
            isOvRoute: TvvStandaardRoute.isOVRoute(dto.voertuigSoort, dto.organisatie),
            ovLijnNummer: dto.ovLijnNummer,
            standaardRoute: dto.standaardRoute ? TvvStandaardRoute.fromDto(dto.standaardRoute) : null,
        };
    },

    mapAndSortEtas(dtoEtas: EtaDto[] | undefined): BaseBusEta[] | undefined {
        if (!dtoEtas) return undefined;

        return dtoEtas
            .map((eta) => ({
                stationCode: eta.stationcode,
                tijd: new Date(eta.tijd),
                bandbreedte: TvvBus.getBandbreedte(eta.tijd),
            }))
            .sort((a, b) => a.tijd.getTime() - b.tijd.getTime());
    },

    getBeginStationCode({ stationCodes }: Pick<TvvBesteldeRoute, "stationCodes">): string {
        return stationCodes[0] ?? "";
    },

    getEindStationCode({ stationCodes }: Pick<TvvBesteldeRoute, "stationCodes">): string {
        return stationCodes.at(-1) ?? "";
    },

    getBussenOpEindStation({
        bussenBesteld,
        aantalBussenOpBeginStation,
    }: Pick<TvvBesteldeRoute, "bussenBesteld" | "aantalBussenOpBeginStation">): number {
        return bussenBesteld - aantalBussenOpBeginStation;
    },

    getBussenForStation<T extends BaseBusEta>({ bussen }: { bussen?: T[] }, stationCode: string): T[] {
        return bussen?.filter((bus) => bus.stationCode === stationCode) ?? [];
    },

    getTotalBussen({ bussen }: Pick<TvvBesteldeRoute, "bussen">): number {
        return bussen?.length ?? 0;
    },

    actieveAndAfgeschaaldeBussen({ bussen }: Pick<TvvBesteldeRoute, "bussen">): [TvvBus[], TvvBus[]] {
        const actieveBussen = bussen.filter((bus) => bus.status === "Actief");
        const afgeschaaldeBussen = bussen.filter((bus) => bus.status === "Afgeschaald");
        return [actieveBussen, afgeschaaldeBussen];
    },

    isComplete({ bussen, bussenBesteld }: Pick<TvvBesteldeRoute, "bussen" | "bussenBesteld">): boolean {
        const actieveBussen = bussen.filter((bus) => bus.status === "Actief");
        return actieveBussen.length >= bussenBesteld;
    },

    moetAfgeschaaldWorden({ bussen, bussenBesteld }: Pick<TvvBesteldeRoute, "bussen" | "bussenBesteld">): boolean {
        const actieveBussen = bussen.filter((bus) => bus.status === "Actief");
        return actieveBussen.length > bussenBesteld;
    },

    isOVRoute(voertuigSoort: Voertuigsoort) {
        return voertuigSoort.startsWith("OV");
    },

    isNietGestoptOfStopAangevraagd({ status }: Pick<TvvBesteldeRoute, "status">): boolean {
        return status !== TVVBestelStatus.StopAangevraagd && status !== TVVBestelStatus.Gestopt;
    },

    routeEditable({ status }: Pick<TvvBesteldeRoute, "status">): boolean {
        return status !== TVVBestelStatus.Gestopt && status !== TVVBestelStatus.StopAangevraagd;
    },
};

export type TvvBus = {
    id: number;
    stationCode: string;
    tijd: Date;
    bandbreedte: string;
    status: "Aangevraagd" | "Actief" | "Afgeschaald" | "Gestopt";
};

export const TvvBus = {
    fromDto: (dto: TvvBusDto): TvvBus => ({
        id: dto.id,
        stationCode: dto.stationcode,
        tijd: new Date(dto.eta),
        status: dto.status,
        bandbreedte: TvvBus.getBandbreedte(dto.eta),
    }),
    getShortLabel: (bus: TvvBus): string => `ETA ${formatDateTimeRelative(bus.tijd)}`,
    getBandbreedte(timeStr: string): string {
        const time = new Date(timeStr);
        const roundedTime = setMinutes(startOfMinute(time), Math.floor(getMinutes(time) / 5) * 5);

        const bottom = subMinutes(roundedTime, 5);
        const top = addMinutes(roundedTime, 25);

        return `${hourFormatInAmsterdam(bottom)} - ${hourFormatInAmsterdam(top)}`;
    },
};
